import { useCallback, useEffect, useState } from "react";
import { listJobs } from "./thailandJobsApi.js";

export const THAILAND_JOBS_DEFAULT_PAGE_SIZE = 25;

/**
 * Fetches GET /api/backoffice/thailand-jobs/jobs with real server-side
 * pagination/filtering - mirrors useThailandJobEmployersList.js. Refetches
 * every time `filters` changes (page, pageSize, search, jobStatus,
 * demandStatus, employerId, agentId,
 * superAgentOrganizationId, plcOrganizationId, skillCode), because the
 * Jobs list can hold far more rows than fits in one page and must never
 * fetch everything just to filter client-side.
 *
 * `filters` must be a stable object reference (the caller should build it
 * with useMemo) - the effect depends on the object directly so it only
 * re-runs when a real filter value actually changes, not on every render.
 * Search debouncing is the caller's responsibility, not this hook's.
 */
export function useThailandJobsList(filters) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Previous data (if any) stays visible while a refetch is in flight -
    // this is what lets the page keep the table/KPI strip mounted and show
    // just an inline loading indicator on a filter change, instead of
    // replacing the whole screen with a spinner every keystroke/click.
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));

    listJobs(filters)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
